import type { ZodType } from "zod";
import type { CharacterEnvelope, CharacterKind } from "./envelope.js";
import type { ValidationResult } from "./validation.js";

/**
 * The contract a ruleset implements so the app can drive it generically.
 * Leviathan (Captains) implements this first; Interceptor (Pilots) implements
 * the same interface later, which is what keeps the second module cheap.
 *
 * This shape will firm up once the Leviathan rules document lands -- in
 * particular `derive` and the choice/budget model. It is deliberately thin
 * for now rather than speculatively detailed.
 */
export interface RulesPack<TCharacter, TDerived = Record<string, number>> {
  /** Stable slug stored in the envelope, e.g. "leviathan". */
  readonly id: string;
  /** Semver-ish; bumped when the rules data changes in a breaking way. */
  readonly version: string;
  readonly displayName: string;
  readonly kind: CharacterKind;

  /** Runtime schema for the character document this ruleset owns. */
  readonly schema: ZodType<TCharacter>;

  /** A fresh, empty-but-valid-shaped character to start the wizard from. */
  createBlank(): TCharacter;

  /**
   * Full legality pass. Called on every edit, so it must be cheap and pure.
   * Returns both blocking errors and advisory warnings.
   */
  validate(character: TCharacter): ValidationResult;

  /** Computed values (derived stats, remaining budget) for display. */
  derive(character: TCharacter): TDerived;

  /** Short label for lists and the export filename. */
  describe(character: TCharacter): string;
}

/** Wraps a ruleset's character in an envelope stamped with that ruleset's identity. */
export function envelopeFor<TCharacter>(
  pack: RulesPack<TCharacter, unknown>,
  character: TCharacter,
  options: { id?: string; now?: Date } = {},
): CharacterEnvelope<TCharacter> {
  return {
    schemaVersion: 1,
    kind: pack.kind,
    id: options.id ?? crypto.randomUUID(),
    createdAt: (options.now ?? new Date()).toISOString(),
    updatedAt: (options.now ?? new Date()).toISOString(),
    rulesetId: pack.id,
    rulesetVersion: pack.version,
    data: character,
  };
}
